import React, { useRef, useMemo, useState, useEffect } from 'react';
import i18n from 'i18n-js';

import { theme } from 'config/theme';
import {
    areaBBoxPx,
    computeRoom,
    EditorArea,
    Point,
    polygonPointsPx,
    RoomRect,
    SizeMeters,
    SVG_HEIGHT,
    SVG_WIDTH,
} from '../mapGeometry';
import {
    MapOpening,
    MapOpeningKind,
    OPENING_BAR_LENGTH,
    OPENING_BAR_WIDTH,
    openingName,
    openingOutlinePoint,
    openingPositionAt,
} from '../openings';
import { GRID_SNAP_METERS } from '../roomOutline';
import { MapFeature } from '../features';
import FeatureFigure from './FeatureFigure';

export { SVG_WIDTH, SVG_HEIGHT };

const WALL_STROKE = 2;
const WALL_COLOR = theme.palette.grey['800'];
const MAGENTA = theme.palette.primary.main; // #99567E
const GRID_COLOR = '#e4e4e4';
// On-screen spacing below which the grid doubles its step so it never turns solid grey.
const GRID_MIN_GAP = 8;
const SCALE_BAR_INSET = 16;
// Leaves room under the scale bar for a zoom control laid over the canvas corner.
const SCALE_BAR_BOTTOM_INSET = 52;
const SCALE_BAR_TICK = 4;
const SCALE_BAR_COLOR = theme.palette.grey['800'];
const CANVAS_BG = '#fafafa';
const DIMMED_FEATURE_OPACITY = 0.8;

export const OPENING_COLORS: Record<MapOpeningKind, string> = {
    Entrance: '#2E7D32',
    Exit: theme.palette.error.dark,
};
export const OPENING_PREFIX_KEY: Record<MapOpeningKind, string> = {
    Entrance: 'tableMaps.wizard.features.inPrefix',
    Exit: 'tableMaps.wizard.features.outPrefix',
};
const OPENING_ARROW_NEAR = 10;
const OPENING_ARROW_FAR = 26;
const OPENING_ARROW_HEAD = 5;
const OPENING_LABEL_OFFSET = 36;
const OPENING_HANDLE_RADIUS = 16;

interface MapEditorCanvasProps {
    gridEnabled: boolean;
    zoom: number;
    size: SizeMeters;
    area: EditorArea;
    openings: MapOpening[];
    features: MapFeature[];
    isFeaturesDimmed?: boolean;
    isPlacingOpening?: boolean;
    onOutlineClick?: (position: Point) => void;
    onOpeningMove?: (id: string, position: Point) => void;
    onOpeningSelect?: (id: string) => void;
    selectedOpeningId?: string | null;
    children?: React.ReactNode;
    /** Optional external ref so callers can do their own clientToSVG conversions */
    svgRef?: React.RefObject<SVGSVGElement>;
    /**
     * When true: the global rectangle is stroked as a dashed guide behind the
     * area outline, so offset/size fields measured against it have a visual
     * referent. Pass only on the Map Configuration step.
     */
    showGlobalRectGuide?: boolean;
    panOnEmptyDrag?: boolean;
    isDrawMode?: boolean;
    showMeasureGuides?: boolean;
}

export function isTextEntryTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** Convert a client-space pointer event position to SVG user units */
export function clientToSVG(
    svg: SVGSVGElement,
    clientX: number,
    clientY: number
): { x: number; y: number } {
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const inv = ctm.inverse();
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const svgPt = pt.matrixTransform(inv);
    return { x: svgPt.x, y: svgPt.y };
}

export function svgToMetres(svgPt: Point, room: RoomRect): Point {
    return { x: (svgPt.x - room.x) / room.pxPerMeter, y: (svgPt.y - room.y) / room.pxPerMeter };
}

/** Starts on a grid line, so with a grid step that divides 1 m both ends of the bar land on one. */
export function scaleBarOrigin(
    left: number,
    bottom: number,
    gridOrigin: Point,
    step: number
): Point {
    return {
        x: gridOrigin.x + Math.ceil((left - gridOrigin.x) / step) * step,
        y: gridOrigin.y + Math.floor((bottom - gridOrigin.y) / step) * step,
    };
}

function metresToPx(p: Point, room: RoomRect): Point {
    return { x: room.x + p.x * room.pxPerMeter, y: room.y + p.y * room.pxPerMeter };
}

function offsetPoint(p: Point, direction: Point, distance: number): Point {
    return { x: p.x + direction.x * distance, y: p.y + direction.y * distance };
}

/** Renders the area outline as a stroked shape — one wall segment per kind. */
function AreaOutline({
    area,
    room,
    dimmed,
}: {
    area: EditorArea;
    room: RoomRect;
    dimmed: boolean;
}) {
    const stroke = dimmed ? '#ccaabb' : WALL_COLOR;
    if (area.kind === 'Ellipse') {
        const bbox = areaBBoxPx(area, room);
        return (
            <ellipse
                cx={bbox.x + bbox.width / 2}
                cy={bbox.y + bbox.height / 2}
                rx={bbox.width / 2}
                ry={bbox.height / 2}
                fill="none"
                stroke={stroke}
                strokeWidth={WALL_STROKE}
            />
        );
    }
    if (area.kind === 'Polygon') {
        const points = polygonPointsPx(area, room);
        return (
            <polygon
                points={points.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={stroke}
                strokeWidth={WALL_STROKE}
                strokeLinejoin="round"
            />
        );
    }
    const bbox = areaBBoxPx(area, room);
    return (
        <rect
            x={bbox.x}
            y={bbox.y}
            width={bbox.width}
            height={bbox.height}
            fill="none"
            stroke={stroke}
            strokeWidth={WALL_STROKE}
            strokeLinecap="square"
        />
    );
}

function AreaOutlineHitbox({
    area,
    room,
    onPointerDown,
}: {
    area: EditorArea;
    room: RoomRect;
    onPointerDown: (e: React.PointerEvent<SVGElement>) => void;
}) {
    const common = {
        fill: 'none' as const,
        stroke: 'transparent',
        strokeWidth: 24,
        style: { cursor: 'crosshair' },
        onPointerDown,
    };
    if (area.kind === 'Ellipse') {
        const bbox = areaBBoxPx(area, room);
        return (
            <ellipse
                cx={bbox.x + bbox.width / 2}
                cy={bbox.y + bbox.height / 2}
                rx={bbox.width / 2}
                ry={bbox.height / 2}
                {...common}
            />
        );
    }
    if (area.kind === 'Polygon') {
        const points = polygonPointsPx(area, room);
        return <polygon points={points.map((p) => `${p.x},${p.y}`).join(' ')} {...common} />;
    }
    const bbox = areaBBoxPx(area, room);
    return <rect x={bbox.x} y={bbox.y} width={bbox.width} height={bbox.height} {...common} />;
}

/**
 * An entrance/exit drawn at a px point on the outline: a coloured bar along the wall, an
 * arrow outside it (into the room for an entrance, out of it for an exit) and the label
 * beyond the arrow. normal/tangent make it work for any wall direction.
 */
function OpeningMarker({
    pointPx,
    normal,
    tangent,
    kind,
    text,
    isSelected,
}: {
    pointPx: Point;
    normal: Point;
    tangent: Point;
    kind: MapOpeningKind;
    text: string;
    isSelected: boolean;
}) {
    const color = OPENING_COLORS[kind];
    const barFrom = offsetPoint(pointPx, tangent, -OPENING_BAR_LENGTH / 2);
    const barTo = offsetPoint(pointPx, tangent, OPENING_BAR_LENGTH / 2);
    const near = offsetPoint(pointPx, normal, OPENING_ARROW_NEAR);
    const far = offsetPoint(pointPx, normal, OPENING_ARROW_FAR);
    const [tail, tip] = kind === 'Entrance' ? [far, near] : [near, far];
    const headBase = offsetPoint(
        tip,
        normal,
        kind === 'Entrance' ? OPENING_ARROW_HEAD : -OPENING_ARROW_HEAD
    );
    const headA = offsetPoint(headBase, tangent, OPENING_ARROW_HEAD);
    const headB = offsetPoint(headBase, tangent, -OPENING_ARROW_HEAD);
    const labelAt = offsetPoint(pointPx, normal, OPENING_LABEL_OFFSET);
    const textAnchor = normal.x > 0.5 ? 'start' : normal.x < -0.5 ? 'end' : 'middle';

    return (
        <g pointerEvents="none">
            {isSelected && (
                <circle
                    cx={pointPx.x}
                    cy={pointPx.y}
                    r={OPENING_BAR_LENGTH / 2 + 6}
                    fill="none"
                    stroke={MAGENTA}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                />
            )}
            <line
                x1={barFrom.x}
                y1={barFrom.y}
                x2={barTo.x}
                y2={barTo.y}
                stroke={color}
                strokeWidth={OPENING_BAR_WIDTH}
            />
            <path
                d={`M ${tail.x} ${tail.y} L ${tip.x} ${tip.y} M ${headA.x} ${headA.y} L ${tip.x} ${tip.y} L ${headB.x} ${headB.y}`}
                fill="none"
                stroke={color}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
            />
            <text
                x={labelAt.x}
                y={labelAt.y}
                textAnchor={textAnchor}
                dominantBaseline="central"
                fontSize={11}
                fontWeight={600}
                fill={color}
                stroke="white"
                strokeWidth={3}
                paintOrder="stroke"
                fontFamily="-apple-system, BlinkMacSystemFont, Roboto, Segoe UI, sans-serif"
            >
                {text}
            </text>
        </g>
    );
}

// Minimum SVG units of room that must remain visible after panning
const PAN_MIN_VISIBLE = 80;
// Pointer movement (client px) below which a pointerdown→up is treated as a click
const PAN_CLICK_THRESHOLD = 3;

/**
 * Clamp a pan offset so at least PAN_MIN_VISIBLE SVG units of the room stay
 * inside the viewport. When zoom <= 1 the entire map fits, so pan is always 0.
 */
function clampPan(
    px: number,
    py: number,
    currentZoom: number,
    room: RoomRect
): { x: number; y: number } {
    if (currentZoom <= 1) return { x: 0, y: 0 };
    const vbW = SVG_WIDTH / currentZoom;
    const vbH = SVG_HEIGHT / currentZoom;
    const vbX0 = (SVG_WIDTH - vbW) / 2;
    const vbY0 = (SVG_HEIGHT - vbH) / 2;
    // viewBox left = vbX0 - panX, right = vbX0 - panX + vbW
    // Ensure at least PAN_MIN_VISIBLE of the room is visible
    const maxPanX = vbX0 + vbW - room.x - PAN_MIN_VISIBLE;
    const minPanX = vbX0 - (room.x + room.width) + PAN_MIN_VISIBLE;
    const maxPanY = vbY0 + vbH - room.y - PAN_MIN_VISIBLE;
    const minPanY = vbY0 - (room.y + room.height) + PAN_MIN_VISIBLE;
    return {
        x: Math.min(maxPanX, Math.max(minPanX, px)),
        y: Math.min(maxPanY, Math.max(minPanY, py)),
    };
}

function MapEditorCanvas({
    gridEnabled,
    zoom,
    size,
    area,
    openings,
    features,
    isFeaturesDimmed = false,
    isPlacingOpening = false,
    onOutlineClick,
    onOpeningMove,
    onOpeningSelect,
    selectedOpeningId = null,
    children,
    svgRef: externalSvgRef,
    showGlobalRectGuide = false,
    panOnEmptyDrag = true,
    isDrawMode = false,
    showMeasureGuides = false,
}: MapEditorCanvasProps) {
    const internalSvgRef = useRef<SVGSVGElement>(null);
    const svgRef = externalSvgRef ?? internalSvgRef;

    const room = useMemo(() => computeRoom(size), [size.width, size.height]);

    // The dragged marker's <g> is translated imperatively — no re-render on every pointermove.
    const openingDrag =
        useRef<{
            id: string;
            group: SVGGElement;
            startPx: Point;
            startClient: Point;
            moved: boolean;
        } | null>(null);

    // Pan offset in SVG user units.
    // Refs are updated imperatively on every pointermove (no re-render per frame).
    // State is committed once on pointerup to trigger a React re-render.
    const panXRef = useRef(0);
    const panYRef = useRef(0);
    const [panState, setPanState] = useState({ x: 0, y: 0 });

    // Pan drag tracking refs
    const panDragActive = useRef(false);
    const panDidMove = useRef(false);
    const panStartClient = useRef({ x: 0, y: 0 });
    const panStartPan = useRef({ x: 0, y: 0 });

    const isPointerOverRef = useRef(false);
    const isSpaceHeldRef = useRef(false);
    const [isSpaceHeld, setIsSpaceHeld] = useState(false);

    useEffect(() => {
        function handleKeyDown(e: KeyboardEvent) {
            if (e.code !== 'Space' || isTextEntryTarget(e.target)) return;
            if (isPointerOverRef.current) e.preventDefault();
            if (isSpaceHeldRef.current) return;
            isSpaceHeldRef.current = true;
            setIsSpaceHeld(true);
        }
        function releaseSpace() {
            isSpaceHeldRef.current = false;
            setIsSpaceHeld(false);
        }
        function handleKeyUp(e: KeyboardEvent) {
            if (e.code === 'Space') releaseSpace();
        }
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', releaseSpace);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', releaseSpace);
        };
    }, []);

    // Reset / re-clamp pan whenever zoom or the global size changes
    useEffect(() => {
        const clamped = clampPan(panXRef.current, panYRef.current, zoom, room);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        setPanState(clamped);
    }, [zoom, room]); // eslint-disable-line react-hooks/exhaustive-deps

    // Effective viewBox dimensions based on zoom (zoom out = larger viewBox)
    const vbWidth = SVG_WIDTH / zoom;
    const vbHeight = SVG_HEIGHT / zoom;
    const vbX = (SVG_WIDTH - vbWidth) / 2;
    const vbY = (SVG_HEIGHT - vbHeight) / 2;
    const viewBox = `${vbX - panState.x} ${vbY - panState.y} ${vbWidth} ${vbHeight}`;

    function startPan(e: React.PointerEvent<SVGSVGElement>) {
        panDragActive.current = true;
        panDidMove.current = false;
        panStartClient.current = { x: e.clientX, y: e.clientY };
        panStartPan.current = { x: panXRef.current, y: panYRef.current };
        (e.currentTarget as SVGSVGElement).setPointerCapture(e.pointerId);
    }

    // Space+drag / middle-drag take the gesture before any child (table, corner, draw layer) sees it.
    function handleSVGPointerDownCapture(e: React.PointerEvent<SVGSVGElement>) {
        if (e.button !== 1 && !isSpaceHeldRef.current) return;
        e.stopPropagation();
        e.preventDefault();
        startPan(e);
    }

    // Children that own their own drag (tables, opening handles) call stopPropagation so pan never starts from them.
    function handleSVGPointerDown(e: React.PointerEvent<SVGSVGElement>) {
        if (!panOnEmptyDrag || zoom <= 1 || isPlacingOpening || openingDrag.current) return;
        startPan(e);
    }

    function handleSVGPointerMove(e: React.PointerEvent<SVGSVGElement>) {
        if (!panDragActive.current) return;
        const dx = e.clientX - panStartClient.current.x;
        const dy = e.clientY - panStartClient.current.y;
        if (!panDidMove.current && Math.hypot(dx, dy) > PAN_CLICK_THRESHOLD) {
            panDidMove.current = true;
        }
        if (!panDidMove.current) return;

        const svgEl = svgRef.current;
        if (!svgEl) return;
        const rect = svgEl.getBoundingClientRect();
        // Convert client pixels → SVG user units (drag right moves content right = panX increases)
        const scaleX = vbWidth / rect.width;
        const scaleY = vbHeight / rect.height;
        const rawX = panStartPan.current.x + dx * scaleX;
        const rawY = panStartPan.current.y + dy * scaleY;
        const clamped = clampPan(rawX, rawY, zoom, room);
        panXRef.current = clamped.x;
        panYRef.current = clamped.y;
        // Imperative update — no setState, no re-render per frame
        svgEl.setAttribute(
            'viewBox',
            `${vbX - clamped.x} ${vbY - clamped.y} ${vbWidth} ${vbHeight}`
        );
        svgEl.style.cursor = 'grabbing';
    }

    function handleSVGPointerUp() {
        if (!panDragActive.current) return;
        panDragActive.current = false;
        if (panDidMove.current) {
            // Commit to state — triggers React re-render which also resets cursor via style prop
            setPanState({ x: panXRef.current, y: panYRef.current });
        }
    }

    function pointerOpeningPosition(clientX: number, clientY: number): Point | null {
        if (!svgRef.current) return null;
        const svgPt = clientToSVG(svgRef.current, clientX, clientY);
        return openingPositionAt(svgToMetres(svgPt, room), size, area);
    }

    function handleOutlineClick(e: React.PointerEvent<SVGElement>) {
        const position = pointerOpeningPosition(e.clientX, e.clientY);
        if (position) onOutlineClick?.(position);
    }

    function handleOpeningDragStart(
        id: string,
        pointPx: Point,
        e: React.PointerEvent<SVGCircleElement>
    ) {
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        openingDrag.current = {
            id,
            group: e.currentTarget.parentNode as SVGGElement,
            startPx: pointPx,
            startClient: { x: e.clientX, y: e.clientY },
            moved: false,
        };
    }

    function handleOpeningDragMove(e: React.PointerEvent<SVGCircleElement>) {
        const drag = openingDrag.current;
        if (!drag) return;
        const dClient = Math.hypot(e.clientX - drag.startClient.x, e.clientY - drag.startClient.y);
        if (!drag.moved && dClient <= PAN_CLICK_THRESHOLD) return;
        const position = pointerOpeningPosition(e.clientX, e.clientY);
        if (!position) return;
        drag.moved = true;
        const pointPx = metresToPx(
            { x: position.x * size.width, y: position.y * size.height },
            room
        );
        drag.group.setAttribute(
            'transform',
            `translate(${pointPx.x - drag.startPx.x}, ${pointPx.y - drag.startPx.y})`
        );
    }

    function handleOpeningDragEnd(e: React.PointerEvent<SVGCircleElement>) {
        const drag = openingDrag.current;
        if (!drag) return;
        openingDrag.current = null;
        drag.group.removeAttribute('transform');
        onOpeningSelect?.(drag.id);
        const position = drag.moved ? pointerOpeningPosition(e.clientX, e.clientY) : null;
        if (position) onOpeningMove?.(drag.id, position);
    }

    // Also fires after a normal pointerup, when the ref is already cleared.
    function handleOpeningDragCancel() {
        const drag = openingDrag.current;
        if (!drag) return;
        openingDrag.current = null;
        drag.group.removeAttribute('transform');
    }

    // Full-canvas grid — rendered as very first child so it sits behind everything
    const fullCanvasGrid = useMemo(
        () =>
            gridEnabled ? (
                <rect
                    x={-SVG_WIDTH * 2}
                    y={-SVG_HEIGHT * 2}
                    width={SVG_WIDTH * 5}
                    height={SVG_HEIGHT * 5}
                    fill="url(#lineGrid)"
                />
            ) : null,
        [gridEnabled]
    );

    let gridStepPx = GRID_SNAP_METERS * room.pxPerMeter;
    while (gridStepPx * zoom < GRID_MIN_GAP) gridStepPx *= 2;
    const unit = 1 / zoom;
    const { x: scaleBarX, y: scaleBarY } = scaleBarOrigin(
        vbX - panState.x + SCALE_BAR_INSET * unit,
        vbY - panState.y + vbHeight - SCALE_BAR_BOTTOM_INSET * unit,
        room,
        gridStepPx
    );

    const canPlainDragPan = panOnEmptyDrag && zoom > 1;
    const svgCursor = isPlacingOpening
        ? 'crosshair'
        : isSpaceHeld || canPlainDragPan
        ? 'grab'
        : 'default';

    return (
        <svg
            ref={svgRef}
            viewBox={viewBox}
            style={{
                width: '100%',
                height: '100%',
                display: 'block',
                background: CANVAS_BG,
                cursor: svgCursor,
            }}
            onPointerDownCapture={handleSVGPointerDownCapture}
            onPointerDown={handleSVGPointerDown}
            onPointerMove={handleSVGPointerMove}
            onPointerUp={handleSVGPointerUp}
            onPointerCancel={handleSVGPointerUp}
            onPointerEnter={() => {
                isPointerOverRef.current = true;
            }}
            onPointerLeave={() => {
                isPointerOverRef.current = false;
            }}
        >
            <defs>
                {/* Anchored on the room origin so cells line up with its walls. */}
                <pattern
                    id="lineGrid"
                    x={room.x % gridStepPx}
                    y={room.y % gridStepPx}
                    width={gridStepPx}
                    height={gridStepPx}
                    patternUnits="userSpaceOnUse"
                >
                    {/* Each tile draws the inner half of all four edges, so a 1 px line sits centred on the grid coordinate */}
                    <rect
                        width={gridStepPx}
                        height={gridStepPx}
                        fill="none"
                        stroke={GRID_COLOR}
                        strokeWidth={unit}
                    />
                </pattern>
            </defs>

            {/* Full-canvas grid background — behind walls and tables */}
            {fullCanvasGrid}

            {/* Global rectangle guide — the frame offset/size fields are measured against */}
            {showGlobalRectGuide && (
                <rect
                    x={room.x}
                    y={room.y}
                    width={room.width}
                    height={room.height}
                    fill="none"
                    stroke={theme.palette.grey['400']}
                    strokeWidth={1}
                    strokeDasharray="4 4"
                />
            )}

            {/* Area outline (the room's walls) */}
            {!isDrawMode && <AreaOutline area={area} room={room} dimmed={isPlacingOpening} />}

            <g pointerEvents="none" opacity={isFeaturesDimmed ? DIMMED_FEATURE_OPACITY : 1}>
                {features.map((feature) => (
                    <g
                        key={feature.id}
                        transform={`translate(${room.x + feature.position.x * room.width}, ${
                            room.y + feature.position.y * room.height
                        })`}
                    >
                        <FeatureFigure feature={feature} pxPerMeter={room.pxPerMeter} />
                    </g>
                ))}
            </g>

            {/* Children (table placement layer) */}
            {children}

            {/* Entrances / exits — above the children so their drag handles stay reachable */}
            {!isDrawMode &&
                openings.map((opening) => {
                    const outline = openingOutlinePoint(opening, size, area);
                    const pointPx = metresToPx(outline.point, room);
                    return (
                        <g key={opening.id}>
                            <OpeningMarker
                                pointPx={pointPx}
                                normal={outline.normal}
                                tangent={outline.tangent}
                                kind={opening.kind}
                                text={`${i18n.t(OPENING_PREFIX_KEY[opening.kind])} · ${openingName(
                                    openings,
                                    opening
                                )}`}
                                isSelected={opening.id === selectedOpeningId}
                            />
                            {onOpeningMove && (
                                <circle
                                    cx={pointPx.x}
                                    cy={pointPx.y}
                                    r={OPENING_HANDLE_RADIUS}
                                    fill="transparent"
                                    style={{ cursor: 'grab' }}
                                    onPointerDown={(e) =>
                                        handleOpeningDragStart(opening.id, pointPx, e)
                                    }
                                    onPointerMove={handleOpeningDragMove}
                                    onPointerUp={handleOpeningDragEnd}
                                    onPointerCancel={handleOpeningDragCancel}
                                    onLostPointerCapture={handleOpeningDragCancel}
                                />
                            )}
                        </g>
                    );
                })}

            {isPlacingOpening && (
                <AreaOutlineHitbox area={area} room={room} onPointerDown={handleOutlineClick} />
            )}

            {showMeasureGuides && (
                <g pointerEvents="none">
                    <path
                        d={`M ${scaleBarX} ${scaleBarY - SCALE_BAR_TICK * unit} V ${
                            scaleBarY + SCALE_BAR_TICK * unit
                        } M ${scaleBarX} ${scaleBarY} H ${scaleBarX + room.pxPerMeter} M ${
                            scaleBarX + room.pxPerMeter
                        } ${scaleBarY - SCALE_BAR_TICK * unit} V ${
                            scaleBarY + SCALE_BAR_TICK * unit
                        }`}
                        fill="none"
                        stroke={SCALE_BAR_COLOR}
                        strokeWidth={2 * unit}
                    />
                    <text
                        x={scaleBarX + room.pxPerMeter + 6 * unit}
                        y={scaleBarY}
                        dominantBaseline="central"
                        fontSize={11 * unit}
                        fill={SCALE_BAR_COLOR}
                    >
                        {i18n.t('tableMaps.wizard.draw.scaleBar')}
                    </text>
                </g>
            )}
        </svg>
    );
}

export default React.memo(MapEditorCanvas);
